import './index.css'

import { IoArrowBack } from '@react-icons/all-files/io5/IoArrowBack'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { Location } from 'react-router-dom'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { BROWSE_LIBRARIES } from '@disco/constants/FeatureTypes'
import {
  clientLibraryDiscoveryPanelFavoriteLoad,
  clientLibraryDiscoveryPanelLoad,
  clientLibraryDiscoveryPanelLoadNext,
  clientLibraryDiscoveryPanelSetLoad,
  clientLibraryDiscoveryPaneRecentLoad,
  clientLibrarySuggestedLibrariesLoad,
} from '@disco/state-management/actions/client-library-discovery-panel-actions'
import { loadCuratedPlaylists } from '@disco/state-management/actions/playlist-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getDiscoveryPanel,
  getDiscoveryPanelFavorite,
  getDiscoveryPanelSet,
  getDiscoveryPaneRecentLoad,
  getSuggestedLibraries,
} from '@disco/state-management/selectors/client-libraries'
import { getCuratedPlaylists } from '@disco/state-management/selectors/playlists'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import { sendDiscoverMixpanelEvent } from '@disco/utils/mixpanel/discover-page'

import type {
  ClientLibraryDiscoveryPanelItem,
  ClientLibraryId,
} from 'types/client-library'
import type { PopoverItemType } from 'types/ui'

import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'

import LibraryGridItem from 'components/common/library-grid-item'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import { Flex, Grid as DSGrid, Heading } from 'components/ds'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ContactUsLink from 'components/ui/contact-us-link'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import EmptyState from 'components/ui/empty-state'
import Grid from 'components/ui/grid'
import Icon from 'components/ui/icon'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Text from 'components/ui/text'
import TextField from 'components/ui/text-field'

import CuratedPlaylistsSection from 'pages/libraries/list/curated-playlists-section'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { getShortlistsOpen } from '@disco/state-management/selectors/app'

import AllDiscoveryLibraries from './all-discovery-libraries'
import CategoryLibraries from './category-libraries'
import DiscoverHeader from './discover-header'
import EasyAccessSwitch from './easy-access-switch'
import { getCatalogBusinessNameFromUrl } from './utils'

// Local interface extending the shared type with additional properties used in this component
// id is required as all valid library items from the API have an id
interface ClientLibrary
  extends Partial<Omit<ClientLibraryDiscoveryPanelItem, 'id'>> {
  id: ClientLibraryDiscoveryPanelItem['id']
  name?: string
}

interface DiscoveryPanelItem {
  id?: string | number
  name: string
  display_name?: string
  client_libraries?: ClientLibrary[]
}

export type ApiFilterValue = '' | 'all' | 'approved' | 'is_favorite'
export type FilterValue = ApiFilterValue | (string & {})

// Valid API filter values - defined outside component to avoid recreating on each render
const API_FILTERS: readonly ApiFilterValue[] = [
  '',
  'all',
  'approved',
  'is_favorite',
]
type Props = {
  children?: React.ReactNode
  location: Location
  showInBrowseLibrariesPage?: boolean
  clientLibrariesTrial?: boolean
}

const isEmptyPanel = pane => {
  if (pane.loading && !pane.loaded) return false
  return (
    Object.keys(pane.items)
      .map(key => pane.items[key])
      .filter(p => {
        const name = p.name || p.display_name || ''
        return !name.toLowerCase().includes('[draft]')
      }).length === 0
  )
}

const BrowseLibraries = (props: Props): React.ReactNode => {
  const browseLibrariesEnabled = useFeatureEnabled(BROWSE_LIBRARIES)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const discoveryPanel = useSelector(getDiscoveryPanel)
  const discoveryPanelFavorite = useSelector(getDiscoveryPanelFavorite)
  const discoveryPaneRecent = useSelector(getDiscoveryPaneRecentLoad)
  const discoveryPanelSet = useSelector(getDiscoveryPanelSet)
  const suggestedLibraries = useSelector(getSuggestedLibraries)
  const clientLibrariesCount = useSelector(
    state => Object.keys(state.clientLibraries.libraries.items).length,
  )
  // Setting up Feature Flag for Discover Page Rebrand work
  const discoverPageRebrand2026 = useSelector(state =>
    getConstanceSetting(state, 'DISCOVER-PAGE-REBRAND-2026'),
  )
  const isPlaylistCreatorOpen = useSelector(getShortlistsOpen)
  const [query, setQuery] = useState<string>('')
  const [submittedQuery, setSubmittedQuery] = useState<string>('')
  const [visibleSearchResults, setVisibleSearchResults] = useState<
    number | undefined
  >(undefined)

  // NOTE: Filter is initialized as empty string but API calls default to 'all'
  // This is intentional for legacy code compatibility and feature flag behavior
  // - Empty string allows feature flag code to distinguish "no filter set"
  // - 'all' fallback maintains backward compatibility with existing API expectations
  const [filter, setFilter] = useState<ApiFilterValue>('')
  const [groupFilter, setGroupFilter] = useState<string>('')
  const [isLoadingNext, setIsLoadingNext] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [panelsSetEmpty, setPanelsSetEmpty] = useState<boolean>(
    isEmptyPanel(discoveryPanelSet),
  )
  const [easyAccess, setEasyAccess] = useState<boolean>(false)
  const pendingSearchRef = useRef<{
    query: string
    filter: string
  } | null>(null)
  const prevSearchRef = useRef<{
    query: string
    filter: string
  } | null>(null)
  const curatedPlaylists = useSelector(getCuratedPlaylists)

  // Helper function to check if a filter is a group filter (local) vs API filter
  const isGroupFilter = (filterValue: string) => {
    return (
      !API_FILTERS.includes(filterValue as ApiFilterValue) &&
      discoverPageRebrand2026
    )
  }

  let featuredCategory = {
    name: 'Featured catalogs',
    id: 'featured',
    client_libraries: [],
  }
  const excludeCategories = []

  if (discoveryPanelSet.loaded) {
    // @ts-expect-error - discoveryPanelSet.items is typed as ClientLibrary[] in redux state but API returns DiscoveryPanelItem[] with category structure
    featuredCategory = discoveryPanelSet.items.find(({ name }) =>
      name.toLowerCase().includes('featured'),
    )

    if (featuredCategory) {
      excludeCategories.push(featuredCategory.name)

      // replace 'Featured libraries' with 'Featured catalogs'
      featuredCategory = {
        ...featuredCategory,
        id: 'featured',
        name:
          featuredCategory?.name === 'Featured libraries'
            ? 'Featured catalogs'
            : featuredCategory.name,
      }
    }
  }

  useEffect(() => {
    const newPanelsSetEmpty = isEmptyPanel(discoveryPanelSet)

    if (newPanelsSetEmpty !== panelsSetEmpty) {
      setPanelsSetEmpty(newPanelsSetEmpty)
    }
  }, [discoveryPanelSet, discoveryPanel, panelsSetEmpty])

  const [searchParams] = useSearchParams()

  // Initialization effect - runs once on mount to sync state from URL params and load initial data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const q = searchParams.get('q') || query
    const rawFilterParam = searchParams.get('filter')
    // Validate filter param against allowed API filter values
    const filterParam: ApiFilterValue | null =
      rawFilterParam && API_FILTERS.includes(rawFilterParam as ApiFilterValue)
        ? (rawFilterParam as ApiFilterValue)
        : null
    const groupParam = searchParams.get('group')
    const initialFilter = filterParam || filter

    if (filterParam && filterParam !== filter) {
      setFilter(filterParam)
    }

    if (groupParam && groupParam !== groupFilter) {
      setGroupFilter(groupParam)
    }

    // Only make API calls if no group filter is selected
    // Under the feature flag, skip loading all catalogs on mount (no "All Catalogs" section)
    if (
      !groupParam &&
      !discoveryPanel.loading &&
      !q &&
      !discoverPageRebrand2026
    ) {
      dispatch(clientLibraryDiscoveryPanelLoad(q, initialFilter))
    }

    if (!discoveryPanelFavorite.loading && !discoveryPanelFavorite.loaded) {
      dispatch(clientLibraryDiscoveryPanelFavoriteLoad())
    }

    if (!discoveryPaneRecent.loading && !discoveryPaneRecent.loaded) {
      dispatch(clientLibraryDiscoveryPaneRecentLoad())
    }

    if (!discoveryPanelSet.loading && !discoveryPanelSet.loaded) {
      dispatch(clientLibraryDiscoveryPanelSetLoad())
    }

    if (!curatedPlaylists.loading && !curatedPlaylists.loaded) {
      dispatch(loadCuratedPlaylists())
    }

    if (!suggestedLibraries.loading && !suggestedLibraries.loaded) {
      dispatch(clientLibrarySuggestedLibrariesLoad())
    }

    if (q) {
      setQuery(q)
      setSubmittedQuery(q) // Initialize submitted query from URL
    }

    // Only call discoveryPanel API when no group filter is selected
    // Under the feature flag, call API when there's a search query or an API filter (like is_favorite)
    // Force refresh when filterParam is present (navigated with explicit filter)
    const shouldLoadApi =
      !groupParam &&
      !discoveryPanel.loading &&
      (!discoveryPanel.loaded || filterParam) &&
      (!discoverPageRebrand2026 || q || filterParam)

    if (shouldLoadApi) {
      pendingSearchRef.current = { query: q, filter: initialFilter || 'all' }
      debouncedQuery(q, initialFilter)
    }
  }, [])

  // Effect to handle URL param changes (navigation with filter/group params)
  // This runs when searchParams change after the initial mount
  useEffect(() => {
    const rawFilterParam = searchParams.get('filter')
    const filterParam: ApiFilterValue | null =
      rawFilterParam && API_FILTERS.includes(rawFilterParam as ApiFilterValue)
        ? (rawFilterParam as ApiFilterValue)
        : null
    const groupParam = searchParams.get('group')
    const q = searchParams.get('q') || ''

    // Sync filter state from URL and trigger API call
    if (filterParam) {
      setFilter(filterParam)
      // Always trigger API call when there's a filter param (e.g., is_favorite)
      if (!groupParam && discoverPageRebrand2026) {
        dispatch(clientLibraryDiscoveryPanelLoad(q, filterParam))
      }
    } else if (!groupParam) {
      // Clear filter when navigating without filter or group param
      setFilter('')
    }

    // Sync group filter state from URL
    if (groupParam) {
      setGroupFilter(groupParam)
    } else {
      setGroupFilter('')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const loadingNextRef = useRef(false)
  useEffect(() => {
    if (!discoveryPanel.loading) {
      setIsSearching(false)
      loadingNextRef.current = false
    }
  }, [discoveryPanel.loading])
  const debouncedQuery = useRef(
    debounce((query, filter) => {
      setIsSearching(false)
      dispatch(clientLibraryDiscoveryPanelLoad(query, filter || 'all'))

      if (query && props.showInBrowseLibrariesPage) {
        const params = new URLSearchParams()
        params.set('q', query)
        if (filter) params.set('filter', filter)
        navigate(`/discover/search?${params.toString()}`)
      }
    }, 300),
  ).current

  // Filter options for old path (EllipsisMenu)
  const FILTER_OPTIONS: PopoverItemType[] = useMemo(
    () => [
      {
        label: 'All Catalogs',
        selected: !filter || filter === 'all',
        value: 'all',
      },
      {
        label: 'Catalogs I have access to',
        selected: filter === 'approved',
        value: 'approved',
      },
      {
        label: 'Favorites',
        selected: filter === 'is_favorite',
        value: 'is_favorite',
      },
    ],
    [filter],
  )

  // Category names for DiscoverHeader filter dropdown (feature flag path)
  const categoryNames = useMemo(() => {
    if (!discoveryPanelSet.loaded || !Array.isArray(discoveryPanelSet.items)) {
      return []
    }
    return discoveryPanelSet.items
      .filter(
        (item: DiscoveryPanelItem) =>
          item.name &&
          !item.name.toLowerCase().includes('[draft]') &&
          !item.name.toLowerCase().includes('featured'),
      )
      .map((item: DiscoveryPanelItem) => item.name)
  }, [discoveryPanelSet])

  // Compute filtered results for group filters
  const groupFilteredResults = useMemo(() => {
    if (!discoverPageRebrand2026 || !groupFilter || !discoveryPanelSet.loaded) {
      return null
    }

    // Validate that discoveryPanelSet.items is an array
    if (!Array.isArray(discoveryPanelSet.items)) {
      return []
    }

    // Explicitly type items as DiscoveryPanelItem[]
    const items: DiscoveryPanelItem[] =
      discoveryPanelSet.items as DiscoveryPanelItem[]

    // Find the selected group - handle "Featured catalogs" specially since the API
    // might return "Featured libraries" which gets renamed in the UI
    let selectedGroup = items.find(
      (item: DiscoveryPanelItem) => item.name === groupFilter,
    )

    // If not found and looking for "Featured catalogs", try matching any featured category
    if (!selectedGroup && groupFilter === 'Featured catalogs') {
      selectedGroup = items.find((item: DiscoveryPanelItem) =>
        item.name?.toLowerCase().includes('featured'),
      )
    }

    if (!selectedGroup?.client_libraries) {
      return []
    }

    // Validate that client_libraries is an array
    if (!Array.isArray(selectedGroup.client_libraries)) {
      return []
    }

    let results = selectedGroup.client_libraries.filter(
      (lib: ClientLibrary) => lib && lib.display_name,
    )

    // Filter by search query only when search has been submitted
    if (submittedQuery) {
      const lowerQuery = submittedQuery.toLowerCase()
      results = results.filter((lib: ClientLibrary) => {
        if (!lib) return false
        const displayName = (lib.display_name || '').toLowerCase()
        const name = (lib.name || '').toLowerCase()
        return displayName.includes(lowerQuery) || name.includes(lowerQuery)
      })
    }

    // Filter by easy access if enabled
    if (easyAccess) {
      results = results.filter(
        (lib: ClientLibrary) => lib && lib.status === 'approved',
      )
    }
    return results
  }, [
    discoverPageRebrand2026,
    groupFilter,
    discoveryPanelSet,
    submittedQuery,
    easyAccess,
  ])

  useEffect(() => {
    if (groupFilteredResults !== null) {
      setVisibleSearchResults(groupFilteredResults.length)
    }
  }, [groupFilteredResults])

  const searchQueryParams = useMemo(() => {
    if (visibleSearchResults === undefined) return undefined

    if (!pendingSearchRef.current && location.state) {
      pendingSearchRef.current = {
        query: location.state?.query,
        filter: location.state?.filter,
      }
    }

    if (pendingSearchRef.current) {
      const { query: searchQuery, filter: searchFilter } =
        pendingSearchRef.current
      const resultCount = visibleSearchResults

      prevSearchRef.current = {
        query: searchQuery,
        filter: searchFilter,
      }

      if (!isSearching && !discoveryPanel.loading && discoveryPanel.loaded) {
        return {
          searchQuery,
          searchFilter,
          resultCount,
        }
      }
    }
    return undefined
  }, [
    discoveryPanel.loaded,
    discoveryPanel.loading,
    isSearching,
    location.state,
    visibleSearchResults,
  ])

  useEffect(() => {
    if (searchQueryParams) {
      const { resultCount, searchQuery, searchFilter } = searchQueryParams
      sendDiscoverMixpanelEvent('Search', {
        context: 'Catalog Search',
        searchTerm: searchQuery?.trim(),
        filter: searchFilter,
        result_count: resultCount,
        location: location.state?.searchLocation?.includes('search')
          ? 'Discover Search Results'
          : 'Discover Music',
      })
    }
    pendingSearchRef.current = null
  }, [location.state?.searchLocation, searchQueryParams, visibleSearchResults])

  if (!browseLibrariesEnabled) return null

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQuery(value)

    // For the new discover page, only search on Enter (not debounce)
    if (!discoverPageRebrand2026) {
      setIsLoadingNext(false)
      setIsSearching(true)
      pendingSearchRef.current = { query: value, filter: filter || 'all' }
      debouncedQuery(value, filter)
    }
  }

  const handleSearch = () => {
    // Set submitted query for both filter types
    setSubmittedQuery(query)
    setVisibleSearchResults(undefined) // Reset visible search results when a new search is submitted

    if (groupFilter) {
      // For group filters, no API call needed - just update the computed results
      setIsLoadingNext(false)
      setIsSearching(false)
    } else {
      // For API filters, make the API call
      setIsLoadingNext(false)
      setIsSearching(true)
      dispatch(clientLibraryDiscoveryPanelLoad(query, filter || 'all'))
    }
    if (query || groupFilter || filter) {
      const params = new URLSearchParams()
      if (query) {
        params.set('q', query)
      }
      if (groupFilter) {
        params.set('group', groupFilter)
      } else if (filter) {
        params.set('filter', filter)
      }

      navigate(`/discover/search?${params.toString()}`, {
        state: {
          query,
          filter: groupFilter || filter || 'all',
          searchLocation: location.pathname,
        },
      })
    } else if (!props.showInBrowseLibrariesPage) {
      // No query and no filter on search page — go back to discover
      navigate('/discover')
    }
  }

  const loadNext = (next: string) => {
    if (discoverPageRebrand2026 && loadingNextRef.current) return
    loadingNextRef.current = true
    setIsLoadingNext(true)
    dispatch(clientLibraryDiscoveryPanelLoadNext(next))
  }

  const onEasyAccess = (
    event: React.SyntheticEvent<HTMLButtonElement> | boolean,
  ) => {
    if (typeof event === 'object') {
      event.preventDefault()
    }

    const newEasyAccess = !easyAccess
    setEasyAccess(newEasyAccess)

    // Only update the API filter when no specific filter is active (empty or 'all')
    // For specific filters like 'is_favorite', keep them and handle easyAccess filtering locally
    if (!filter || filter === 'all') {
      const apiFilter = newEasyAccess ? 'approved' : 'all'
      debouncedQuery(query, apiFilter)
    }
  }
  //TODO: Redefine how filters work in new search results inner page
  const handleFilterChange = (value: FilterValue) => {
    if (isGroupFilter(value)) {
      // Handle group filter (local filtering)
      setGroupFilter(value)
      // Reset API filter to 'all' when selecting group filter
      setFilter('all')
      setIsLoadingNext(false)
      setIsSearching(false)
    } else {
      // Handle API filter
      setFilter(value as ApiFilterValue)
      setGroupFilter('') // Clear group filter when selecting API filter

      if (filter !== value) {
        setIsLoadingNext(false)
        setIsSearching(true)
        debouncedQuery(query, value)
      }
    }

    // Update URL to reflect the current filter
    if (discoverPageRebrand2026) {
      const params = new URLSearchParams()
      if (query) params.set('q', query)
      if (isGroupFilter(value)) {
        params.set('group', value)
      } else if (value) {
        params.set('filter', value)
      }
      // set locationState to hold onto search params as URL changes
      navigate(`/discover/search?${params.toString()}`, {
        state: {
          query,
          filter: value || 'all',
          searchLocation: location.pathname,
        },
      })
    }
  }

  const handleClearAll = () => {
    setQuery('')
    setSubmittedQuery('')
    setFilter('')
    setGroupFilter('')
    setIsLoadingNext(false)
    setIsSearching(false)
    debouncedQuery('', 'all')

    // Navigate back to discover page if on search results
    if (!props.showInBrowseLibrariesPage) {
      navigate('/discover')
    }
  }

  const handleClearQuery = () => {
    setQuery('')
    setSubmittedQuery('')

    // Stay on search page if a filter is still active
    if (groupFilter || filter) {
      // Re-fetch API results without query for API filters
      if (!groupFilter) {
        dispatch(clientLibraryDiscoveryPanelLoad('', filter || 'all'))
      }

      const params = new URLSearchParams()
      if (groupFilter) {
        params.set('group', groupFilter)
      } else {
        params.set('filter', filter)
      }
      navigate(`/discover/search?${params.toString()}`)
    } else {
      navigate('/discover')
    }
  }

  const filterEllipsisMenu = (
    <EllipsisMenu
      caption="Filter by"
      options={FILTER_OPTIONS}
      onOptionClick={handleFilterChange}
    />
  )
  const showClearButton = Boolean(query && !props.showInBrowseLibrariesPage)
  const inputSearch = (
    <div className="BrowseLibraries_Input">
      <TextField
        icon={<Icon type="search" className="BrowseLibraries_SearchIcon" />}
        placeholder="Search by genre or rights holder"
        value={query}
        onChange={updateSearchQuery}
        autoFocus={showClearButton}
        nodeRight={
          showClearButton ? (
            <Button
              appearance="icon"
              onClick={() => {
                setQuery('')
                setSubmittedQuery('')
                debouncedQuery('', filter)
                navigate('/discover')
              }}
              icon="close"
            />
          ) : undefined
        }
      />
    </div>
  )
  const easyAccessToggle = (
    <div className="BrowseLibraries_ToggleContainer">
      <EasyAccessSwitch onClick={onEasyAccess} value={easyAccess} />
    </div>
  )
  const categoryLibrariesData = {
    easyAccess,
    query,
  }

  if (props.showInBrowseLibrariesPage) {
    return (
      <>
        <div
          className={`Page Page--discover${
            isPlaylistCreatorOpen ? ' Page--playlistCreatorOpen' : ''
          }`}
        >
          <DocumentHeadTags title="Catalogs | DISCO" />
          {discoverPageRebrand2026 && (
            <DiscoverHeader
              query={query}
              filter={groupFilter || filter}
              categories={categoryNames}
              onQueryChange={updateSearchQuery}
              onFilterChange={handleFilterChange}
              onClearAll={handleClearAll}
              onSearch={handleSearch}
              isSearchPage={false}
            />
          )}
          <div className="Page_Content LibrariesList">
            {discoverPageRebrand2026 && (
              <Heading as="h1" size="8" m="0" mb="5">
                Discover Music
              </Heading>
            )}
            <CuratedPlaylistsSection />
            {!discoverPageRebrand2026 && (
              <div className="LibrariesList_TrialAlertContainer">
                <Alert
                  title="Introducing: Catalogs"
                  status="info"
                  id="browse_libraries"
                  dismissible
                >
                  Browse all your favourite rights holders Catalogs with DISCO
                  Catalogs. Look for the <Icon type="checkbox" height={16} />{' '}
                  icon for Catalogs you’re approved to download from.{' '}
                  <a
                    className="text-link"
                    href="https://www.youtube.com/watch?v=ISaw2UJTAAY"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Watch the video
                  </a>
                </Alert>
              </div>
            )}
            {!discoverPageRebrand2026 && (
              <Heading as="h2" size="6">
                Browse Catalogs
              </Heading>
            )}
            {!discoverPageRebrand2026 && props.clientLibrariesTrial && (
              <Text type="paragraph">
                Browse and access other Catalogs on DISCO from high quality
                labels, publishers, artists and more.
              </Text>
            )}
            {!discoverPageRebrand2026 && (
              <div className="BrowseLibraries_SearchWithActions">
                <div className="flc">
                  {inputSearch}
                  {easyAccessToggle}
                </div>

                <ButtonContainer>
                  {clientLibrariesCount > 0 ? (
                    <Button
                      appearance="primary"
                      caption="Create new Catalog"
                      link="/catalogs/create"
                    />
                  ) : (
                    <ContactUsLink intercomMsg="I`d like to set up a Catalog on my supervisor DISCO">
                      <Button
                        appearance="primary"
                        caption="Set up your Catalog"
                      />
                    </ContactUsLink>
                  )}
                  <EllipsisMenu
                    compact
                    options={[
                      {
                        type: 'link',
                        label: 'Give feedback',
                        link: {
                          url: 'https://form.typeform.com/to/IfV1phtd',
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        },
                      },
                      {
                        type: 'router-link',
                        label: 'Opt out of easy access',
                        link: {
                          url: '/discover/opt-out-of-easy-access',
                        },
                      },
                    ]}
                    icon="ellipsis"
                    hasArrow={false}
                  />
                </ButtonContainer>
              </div>
            )}

            {!panelsSetEmpty && (
              <div className="CategoryLibraries">
                {featuredCategory && featuredCategory.client_libraries && (
                  <CategoryLibraries
                    categories={[featuredCategory]}
                    gridSize="xxlarge"
                    data={categoryLibrariesData}
                    loading={
                      discoveryPanelSet.loading && !discoveryPanelSet.loaded
                    }
                  />
                )}

                <CategoryLibraries
                  categories={[
                    {
                      id: 'favorited',
                      name: 'Your favorited Catalogs',
                      client_libraries: discoveryPanelFavorite.list.map(
                        id => discoveryPanelFavorite.items[id],
                      ),
                    },
                  ]}
                  gridSize="mlarge"
                  data={categoryLibrariesData}
                  loading={
                    discoveryPanelFavorite.loading &&
                    !discoveryPanelFavorite.loaded
                  }
                />
                {!discoverPageRebrand2026 && (
                  <CategoryLibraries
                    categories={[
                      {
                        id: 'recent',
                        name: 'Recently visited Catalogs',
                        client_libraries: discoveryPaneRecent.list.map(
                          id => discoveryPaneRecent.items[id],
                        ),
                      },
                    ]}
                    data={categoryLibrariesData}
                    loading={
                      discoveryPaneRecent.loading && !discoveryPaneRecent.loaded
                    }
                  />
                )}

                <CategoryLibraries
                  // @ts-expect-error - discoveryPanelSet.items is typed as ClientLibrary[] in redux state but API returns DiscoveryPanelItem[] with category structure
                  categories={discoveryPanelSet.items.filter(
                    ({ name }) => !excludeCategories.includes(name),
                  )}
                  data={categoryLibrariesData}
                  loading={
                    discoveryPanelSet.loading && !discoveryPanelSet.loaded
                  }
                />
              </div>
            )}
            {!discoverPageRebrand2026 && (
              <>
                <div className="BrowseLibraries_SplitTitle">
                  <Heading as="h2" size="6">
                    All Catalogs
                  </Heading>
                  <Text type="paragraph">
                    Browse music from over 1000 Catalogs from around the world
                  </Text>
                </div>
                <PageContainer.Section>
                  <AllDiscoveryLibraries
                    isSearching={isSearching}
                    query={query}
                    filter={filter}
                    loadNext={loadNext}
                    libraryGridItemType="shadow"
                    hideLoadingLabel
                    easyAccess={easyAccess}
                    setVisibleSearchResults={setVisibleSearchResults}
                  />
                </PageContainer.Section>

                <div className="BrowseLibraries_Counter">
                  <div className="BrowseLibraries_CounterRow">
                    Viewing {discoveryPanel.list.length}-{discoveryPanel.count}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {props.children}
      </>
    )
  }

  return props.location?.state?.modal && props.children ? (
    props.children
  ) : (
    <div className="Page Page--discover">
      <DocumentHeadTags title="Browse catalogs on DISCO | DISCO" />
      {discoverPageRebrand2026 && (
        <DiscoverHeader
          query={query}
          filter={groupFilter || filter}
          categories={categoryNames}
          onQueryChange={updateSearchQuery}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAll}
          onClearQuery={handleClearQuery}
          onSearch={handleSearch}
          isSearchPage={true}
          easyAccess={easyAccess}
          onEasyAccessChange={onEasyAccess}
        />
      )}
      <div className="Page_Content">
        {!discoverPageRebrand2026 && (
          <PageHeader
            breadcrumbs={{
              content: 'Browse Catalogs',
              link: '/discover',
            }}
            title="Browse Catalogs on DISCO"
            actions={filterEllipsisMenu}
          />
        )}
        {discoverPageRebrand2026 && (
          <Link to="/discover" className="BrowseLibraries_BackButton">
            <IoArrowBack size={16} />
            <span>Back</span>
          </Link>
        )}
        {!discoverPageRebrand2026 && (
          <Row columnsSize={[3, 4]}>
            <div>{inputSearch}</div>
            <div>{easyAccessToggle}</div>
          </Row>
        )}
        {!discoverPageRebrand2026 && query && (
          <Row columnsSize={[12]}>
            <div>
              <Heading as="h2" size="6">
                Your search for ’{query}’
              </Heading>
            </div>
          </Row>
        )}
        {(() => {
          // Show group filtered results when a group filter is selected
          if (discoverPageRebrand2026 && groupFilter) {
            if (!discoveryPanelSet.loaded) {
              return 'Loading...'
            }

            if (!groupFilteredResults || groupFilteredResults.length === 0) {
              return (
                <EmptyState>
                  <img
                    alt=""
                    srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
                    width="177"
                    height="160"
                  />
                  <p>Sorry! No results for that search.</p>
                </EmptyState>
              )
            }
            return (
              <DSGrid columns={{ initial: '1', sm: '2', md: '3' }} gap="4">
                {groupFilteredResults.map((lib: ClientLibrary) => {
                  const businessName = getCatalogBusinessNameFromUrl(
                    lib.public_url || '',
                  )

                  return (
                    <LibraryGridItem
                      key={lib.id}
                      lib={lib as ClientLibraryDiscoveryPanelItem}
                      onClick={() => {
                        sendDiscoverMixpanelEvent('Click Image', {
                          context: 'Search Results',
                          catalogId: lib.id,
                          catalogBusiness: businessName || 'Unknown',
                        })
                      }}
                    />
                  )
                })}
              </DSGrid>
            )
          }

          // Original logic for API-based results
          if ((!isLoadingNext && discoveryPanel.loading) || isSearching) {
            return (
              <div className="BrowseLibraries_LoadingState">Loading...</div>
            )
          }

          return (
            <AllDiscoveryLibraries
              isSearching={isSearching}
              query={query}
              filter={filter}
              loadNext={loadNext}
              easyAccess={easyAccess}
              setVisibleSearchResults={setVisibleSearchResults}
            />
          )
        })()}
      </div>
      {props.children}
    </div>
  )
}

export default BrowseLibraries
